import { LinkOff } from '@mui/icons-material';
import { Alert, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler } from 'react';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { ACTIVE_STATUSES } from '../../../integrations/tanium/enums/TaniumStatus';
import { ProjectTanium } from '../../../integrations/tanium/models/ProjectTanium';
import { ButtonBox, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { STYLES } from './ProjectTaniumSession';

type Props = {
  isLoading: boolean;
  isOrgIntegrationConfigured: boolean;
  projectTanium: ProjectTanium | null;
  canUpdate: boolean;
  handleSave: (computerGroup: string) => void;
  handleTestComputerGroup: (computerGroup: string) => void;
  handleUnlinkTanium: () => void;
};

export const ProjectTaniumConfigForm = ({
  isLoading,
  isOrgIntegrationConfigured,
  projectTanium,
  canUpdate,
  handleSave,
  handleTestComputerGroup,
  handleUnlinkTanium,
}: Props) => {
  const status = projectTanium?.getStatus() ?? null;
  const isRetrievalActive = status !== null && ACTIVE_STATUSES.includes(status);
  const allFieldsDisabled = isLoading || !isOrgIntegrationConfigured || isRetrievalActive || !canUpdate;

  const [compGroup, compGroupComponent, isCompGroupValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('projectIntegrations.tanium.compGroupInputLabel'),
    value: projectTanium?.getComputerGroup() ?? '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      disabled: allFieldsDisabled,
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const canSave = isCompGroupValid && compGroup !== projectTanium?.getComputerGroup();

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    handleSave(compGroup);
  };

  const handleTestClick = () => {
    handleTestComputerGroup(compGroup);
  };

  return (
    <Paper sx={{ height: '100%', p: 1.5 }}>
      <Stack sx={{ padding: 1.5 }} direction={'column'} spacing={2} component="form" onSubmit={onSubmit}>
        <Stack sx={{ minHeight: '19rem' }} direction={'column'} spacing={2}>
          <Typography sx={{ ...STYLES.title, pt: '.375rem' }}>{t('projectIntegrations.tanium.configuration')}</Typography>
          {!isLoading && !isOrgIntegrationConfigured && (
            <Alert color="info" icon={false}>
              {t('projectIntegrations.tanium.notConfigured')}
            </Alert>
          )}
          {compGroupComponent}
          <ButtonBox>
            <Button variant="outlined" sx={{ float: 'left' }} onClick={handleTestClick} disabled={allFieldsDisabled || !isCompGroupValid}>
              {t('projectIntegrations.tanium.testConnection')}
            </Button>
          </ButtonBox>
        </Stack>
        <Divider />
        <ButtonBox>
          {projectTanium !== null && (
            <Button variant="outlined" color="error" sx={{ float: 'left' }} onClick={handleUnlinkTanium} disabled={allFieldsDisabled}>
              <LinkOff sx={{ mr: 1 }} />
              {t('projectIntegrations.tanium.unlink')}
            </Button>
          )}
          <PrimaryButton type="submit" disabled={allFieldsDisabled || !canSave}>
            {t('projectIntegrations.tanium.save')}
          </PrimaryButton>
        </ButtonBox>
      </Stack>
    </Paper>
  );
};
